'use client'

import { useState, useEffect, useRef } from 'react'
import Script from 'next/script'
import { FadeIn } from '@/components/FadeIn'
import { trackEvent } from '@/lib/umami'

const HUBSPOT_CONSENT_KEY = 'hubspot_meeting_consent'

// Extend Window interface for HubSpot Meetings embed helper
declare global {
  interface Window {
    hbspt?: {
      meetings?: {
        create: (selector: string) => void
      }
    }
  }
}

export function HubSpotMeetingEmbed({
  meetingUrl,
  title = 'Online-Termin buchen',
}: {
  meetingUrl: string
  title?: string
}) {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Check for existing consent on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem(HUBSPOT_CONSENT_KEY)
    setHasConsented(storedConsent === 'true')

    // If the HubSpot script was already loaded on a previous navigation,
    // mark it as ready immediately so we can re-embed.
    if (typeof window !== 'undefined' && window.hbspt?.meetings?.create) {
      setScriptLoaded(true)
    }
  }, [])

  // Re-initialize HubSpot when both script is loaded and container is rendered
  useEffect(() => {
    if (hasConsented && scriptLoaded && containerRef.current) {
      // Clear any previous iframe and trigger HubSpot to (re)create it
      const container = containerRef.current
      container.innerHTML = ''

      const timer = setTimeout(() => {
        window.hbspt?.meetings?.create?.('.meetings-iframe-container')
      }, 50)

      return () => clearTimeout(timer)
    }
  }, [hasConsented, scriptLoaded, meetingUrl])

  const handleConsent = () => {
    trackEvent('meeting_consent')
    localStorage.setItem(HUBSPOT_CONSENT_KEY, 'true')
    setHasConsented(true)
  }

  // Show nothing until we've checked localStorage
  if (hasConsented === null) {
    return (
      <FadeIn>
        <h2 className="font-display text-base font-semibold text-zinc-950">
          {title}
        </h2>
        <div className="mt-6 aspect-[4/3] animate-pulse rounded-2xl bg-zinc-100" />
      </FadeIn>
    )
  }

  return (
    <FadeIn>
      <h2 className="font-display text-base font-semibold text-zinc-950">
        {title}
      </h2>
      {hasConsented ? (
        <>
          {/* HubSpot Meetings Embed */}
          <div
            ref={containerRef}
            className="meetings-iframe-container mt-6"
            data-src={`${meetingUrl}?embed=true`}
          />
          <Script
            id="hubspot-meetings-embed"
            src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"
            strategy="lazyOnload"
            data-hubspot-meetings-script="true"
            onLoad={() => setScriptLoaded(true)}
          />
        </>
      ) : (
        /* Consent Overlay */
        <div className="relative mt-6 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id="consent-pattern"
                  x="0"
                  y="0"
                  width="32"
                  height="32"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="16" cy="16" r="1.5" fill="currentColor" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#consent-pattern)" />
            </svg>
          </div>

          <div className="relative flex min-h-[400px] flex-col items-center justify-center px-6 py-12 text-center">
            {/* Icon */}
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-zinc-900">
              <svg
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                />
              </svg>
            </div>

            {/* Text content */}
            <h3 className="font-display text-lg font-semibold text-zinc-950">
              Terminbuchung laden
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-zinc-600">
              Um dir unseren Terminkalender anzuzeigen, nutzen wir HubSpot.
              Dabei werden Cookies gesetzt und Daten an HubSpot übertragen.
            </p>

            {/* Consent button */}
            <button
              onClick={handleConsent}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-zinc-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-zinc-800 focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Akzeptieren & laden
            </button>

            {/* Privacy link */}
            <p className="mt-4 text-xs text-zinc-500">
              Mehr Infos in unserer{' '}
              <a
                href="/datenschutz"
                className="underline underline-offset-2 transition hover:text-zinc-950"
              >
                Datenschutzerklärung
              </a>
            </p>
          </div>
        </div>
      )}
      <div className="mt-6 text-sm text-zinc-600">
        Du siehst die Terminbuchung nicht? Klicke{' '}
        <a
          href="https://info.thepalia.de/meetings/morten-homburg/analyse?utm_source=thepalia.de-terminkalender"
          className="underline underline-offset-2 transition hover:text-zinc-950"
          onClick={() =>
            trackEvent('meeting_open', { source: 'embed_fallback' })
          }
        >
          hier
        </a>
        .
      </div>
    </FadeIn>
  )
}
